/**
 * Maps a kanban API task into a normalized card content object.
 * Includes fields used by TPDD, WB/CM, and GDPR so module wrappers
 * can pick what they need without dropping data.
 */
export const defaultMapTaskContent = (task = {}) => {
    const reportCase = task.report_case || {};

    return {
        id: task.id,
        secondaryId: task.secondary_id,
        title: task.name,
        status: task.status,
        endDate: task.ended_at,
        date: task.ended_at,
        priority: task.priority,
        progressValue: 100,
        supplier: task.supplier,
        isMitigation: task.is_mitigation,
        is_mitigation: task.is_mitigation,
        isRecurring: task.is_recurring,
        recurrenceSecondaryId: task.recurrence_secondary_id,
        relatedRisk: {
            secondaryId: task.relatedRisk?.secondary_id || "",
            isMitigation: task.is_mitigation,
        },
        caseId: reportCase.secondary_case_id,
        primaryCaseId: reportCase.case_id,
        caseTitle: reportCase.description,
        questionnaire: task.questionnaire,
        caseSecondaryId: task.case_secondary_id,
        reportCase: {
            caseId: reportCase.case_id,
            secondaryCaseId: reportCase.secondary_case_id,
            isUnreal: reportCase.is_unreal,
            reportType: reportCase.report_type,
            software: reportCase.software,
            otherAsset: reportCase.other_asset,
            control: reportCase.control,
            tom: reportCase.tom,
        },
        task_secondary_id: task.secondary_id,
        asset_id: task.asset_id,
        risk_secondary_id: task.risk_secondary_id,
        team: [
            {
                id: `task-owner-${reportCase.secondary_case_id || task.id}-${task.ended_at}`,
                type: "Task Owner",
                name: task.owner_info?.name,
                avatar: task.owner_info?.avatar,
            },
            {
                id: `task-manager-${reportCase.secondary_case_id || task.id}-${task.ended_at}`,
                type: "Task Manager",
                name: task.manager_info?.name,
                avatar: task.manager_info?.avatar,
            },
        ],
    };
};

export const DEFAULT_PAGE_SIZE = 20;

export const DEFAULT_COLUMN_STATUSES = [
    "NEW",
    "IN_PROGRESS",
    "REVIEW",
    "CLOSED",
];

/**
 * Normalizes a column API payload (array or object) into one column.
 * When the API returns all columns, pick by `status` — never by title.
 */
export const normalizeColumnPayload = (payload, status) => {
    if (Array.isArray(payload)) {
        if (status != null && status !== "") {
            const matched = payload.find((column) => column?.status === status);

            if (matched) {
                return matched;
            }
        }

        return payload[0] || null;
    }

    if (payload && (Array.isArray(payload.data) || payload.title || payload.status)) {
        return payload;
    }

    return null;
};

export const mapTasksToCards = (tasks = [], mapTaskContent = defaultMapTaskContent) => {
    return (tasks || []).map((task) => ({
        id: task.id,
        content: mapTaskContent(task),
    }));
};

export const createPaginationSentinelCard = (status, direction) => ({
    id: `__kanban_sentinel_${direction}_${status}__`,
    content: {
        __pagination: true,
        direction,
        status,
    },
});

/**
 * Builds a controlled-board column from a paginated API column payload.
 * Uses API `status` for identity/filtering; `title` is display-only.
 */
export const buildColumnFromPayload = ({
    status,
    payload,
    mapTaskContent = defaultMapTaskContent,
    fallbackTitle,
}) => {
    const column = normalizeColumnPayload(payload, status) || {};
    const resolvedStatus = column.status || status;
    const pageIndex = column.pageIndex || 1;
    const pageSize = column.pageSize || DEFAULT_PAGE_SIZE;
    const cards = mapTasksToCards(column.data, mapTaskContent);
    const count = column.count ?? cards.length;
    const hasMore =
        typeof column.hasMore === "boolean"
            ? column.hasMore
            : cards.length >= pageSize;

    return {
        id: resolvedStatus,
        title: column.title || fallbackTitle || resolvedStatus,
        columnsubtitle: `${count}`,
        status: resolvedStatus,
        count,
        pageIndex,
        pageSize,
        hasMore,
        loadingNext: false,
        cards,
    };
};

export const withColumnSentinels = (column) => {
    if (!column) {
        return column;
    }

    const cards = [...(column.cards || [])];

    // Only a bottom sentinel — next pages load via the main page scrollbar.
    if (column.hasMore || column.loadingNext) {
        cards.push(createPaginationSentinelCard(column.status, "next"));
    }

    return {
        ...column,
        cards,
    };
};

export const toBoardColumns = (columns = []) => {
    return columns.map((column) => withColumnSentinels(column));
};

export const getBoardTotalCount = (columns = []) => {
    return columns.reduce((acc, column) => {
        if (typeof column?.count === "number") {
            return acc + column.count;
        }

        const realCards = (column?.cards || []).filter(
            (card) => !card?.content?.__pagination
        );

        return acc + realCards.length;
    }, 0);
};

/**
 * @deprecated Prefer buildColumnFromPayload / toBoardColumns for paginated boards.
 */
export const mapBoardApiDataToColumns = (boardData, mapTaskContent = defaultMapTaskContent) => {
    if (!Array.isArray(boardData)) {
        return [];
    }

    return boardData.map((item, index) => {
        const itemTasks = item?.data || [];
        const count = item?.count ?? itemTasks.length;
        const status = item?.status || index;

        return {
            id: status,
            title: item.title,
            status,
            columnsubtitle: `${count}`,
            cards: mapTasksToCards(itemTasks, mapTaskContent),
        };
    });
};
